/**
 * Browser-side image import: type detection by content (not just extension), size limits,
 * decoding of PNG/JPEG via createImageBitmap and rasterization of SVG via an <img> element
 * (scripts inside SVG never run in an image context). All processing stays on this device.
 */
import { t } from '../i18n/i18n';
import type { Raster } from './raster';

export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_DECODE_PIXELS = 40_000_000;
export const SVG_RASTER_SIZE = 1600;

export type ImageKind = 'png' | 'jpeg' | 'svg';

export class ImportError extends Error {}

export function sniffImageType(bytes: Uint8Array, fileName = ''): ImageKind | null {
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'png';
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
  const head = new TextDecoder().decode(bytes.subarray(0, 2048)).trimStart().toLowerCase();
  if ((head.startsWith('<?xml') || head.startsWith('<svg') || head.startsWith('<!--') || head.startsWith('<!doctype svg')) && head.includes('<svg')) return 'svg';
  if (fileName.toLowerCase().endsWith('.svg') && head.includes('<svg')) return 'svg';
  return null;
}

export const MIME: Record<ImageKind, string> = { png: 'image/png', jpeg: 'image/jpeg', svg: 'image/svg+xml' };

export interface DecodedImage {
  kind: ImageKind;
  mime: string;
  name: string;
  bytes: Uint8Array;
  raster: Raster;
  /** Original intrinsic size (SVG: viewBox/width/height). */
  width: number;
  height: number;
}

export function checkFile(file: { name: string; size: number }): void {
  if (file.size === 0) throw new ImportError(t('The file is empty.'));
  if (file.size > MAX_FILE_BYTES) {
    throw new ImportError(t('The file is {size} MB. The maximum size is {max} MB – save the image at a lower resolution.', { size: (file.size / 1048576).toFixed(1), max: MAX_FILE_BYTES / 1048576 }));
  }
}

/** Parses intrinsic SVG size from width/height or viewBox. */
export function svgSize(text: string): { width: number; height: number } {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const svg = doc.documentElement;
  if (!svg || svg.nodeName.toLowerCase() !== 'svg' || doc.getElementsByTagName('parsererror').length) {
    throw new ImportError(t('The SVG file could not be read. Check that it is valid.'));
  }
  const vb = (svg.getAttribute('viewBox') ?? '').split(/[\s,]+/).map(Number);
  const len = (v: string | null) => (v && !v.endsWith('%') ? parseFloat(v) : NaN);
  let w = len(svg.getAttribute('width'));
  let h = len(svg.getAttribute('height'));
  if (!(w > 0 && h > 0) && vb.length === 4 && vb[2] > 0 && vb[3] > 0) {
    w = vb[2];
    h = vb[3];
  }
  if (!(w > 0 && h > 0)) {
    w = 512;
    h = 512;
  }
  return { width: w, height: h };
}

function toRaster(source: CanvasImageSource, w: number, h: number): Raster {
  const canvas = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : Object.assign(document.createElement('canvas'), { width: w, height: h });
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  if (!ctx) throw new ImportError(t('The browser cannot draw the image (Canvas is missing).'));
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(source, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h);
  return { width: w, height: h, data: data.data };
}

async function rasterizeSvg(text: string, maxSide: number): Promise<{ raster: Raster; width: number; height: number }> {
  const { width, height } = svgSize(text);
  const s = maxSide / Math.max(width, height);
  const w = Math.max(1, Math.round(width * s));
  const h = Math.max(1, Math.round(height * s));
  // ensure the SVG has explicit pixel size so it renders at the requested resolution
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const svg = doc.documentElement;
  if (!svg.getAttribute('viewBox')) svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.setAttribute('width', String(w));
  svg.setAttribute('height', String(h));
  const blob = new Blob([new XMLSerializer().serializeToString(doc)], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode().catch(() => {
      throw new ImportError(t('The SVG file could not be drawn. External resources and scripts are not supported.'));
    });
    return { raster: toRaster(img, w, h), width, height };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function decodeImageFile(file: File): Promise<DecodedImage> {
  checkFile(file);
  const bytes = new Uint8Array(await file.arrayBuffer());
  const kind = sniffImageType(bytes, file.name);
  if (!kind) throw new ImportError(t('This file type is not supported. Use PNG, JPEG or SVG.'));
  if (kind === 'svg') {
    const text = new TextDecoder().decode(bytes);
    const { raster, width, height } = await rasterizeSvg(text, SVG_RASTER_SIZE);
    return { kind, mime: MIME.svg, name: file.name, bytes, raster, width, height };
  }
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(new Blob([bytes], { type: MIME[kind] }), { imageOrientation: 'from-image', premultiplyAlpha: 'none' });
  } catch {
    throw new ImportError(t('The image could not be decoded. The file may be damaged.'));
  }
  try {
    if (bitmap.width * bitmap.height > MAX_DECODE_PIXELS) {
      throw new ImportError(t('The image is {w} × {h} pixels. The maximum is {max} megapixels.', { w: bitmap.width, h: bitmap.height, max: MAX_DECODE_PIXELS / 1e6 }));
    }
    return { kind, mime: MIME[kind], name: file.name, bytes, raster: toRaster(bitmap, bitmap.width, bitmap.height), width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close();
  }
}

/** Decodes stored image bytes (from a saved project). */
export async function decodeStoredImage(bytes: ArrayBuffer, mime: string, name: string): Promise<DecodedImage> {
  return decodeImageFile(new File([bytes], name, { type: mime }));
}
